// Header.js - Responsive Premium Design (Global)
import React, { useState } from 'react';
import { useBooking } from '../../hooks/useBooking';
import { useCurrency } from '../../context/CurrencyContext';
import { useAuth } from '../../hooks/useAuth';
import LoginModal from '../auth/LoginModal';
import RegisterModal from '../auth/RegisterModal';
import { PAGES, SERVICE_TYPES } from '../../utils/constants';

// Import the new logo
import logoMark from '../../assets/images/logo.png';

const Header = ({ transparent = false }) => {
  const { setCurrentPage, setSelectedService, state: { selectedService, currentPage } } = useBooking();
  const { currencies, selectedCurrency, changeCurrency } = useCurrency();
  
  // 🔐 Authentication state
  const { state: authState, openLoginModal, closeLoginModal, openRegisterModal, closeRegisterModal, logout } = useAuth();
  const { isAuthenticated, user, loginModalOpen, registerModalOpen } = authState;
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  
  // Existing state
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);

  // 🔐 Authentication handlers
  const handleSwitchToRegister = () => {
    closeLoginModal();
    openRegisterModal();
  };

  const handleSwitchToLogin = () => {
    closeRegisterModal();
    openLoginModal();
  };

  const handleLogout = () => {
    logout();
    setUserDropdownOpen(false);
    setCurrentPage(PAGES.HOME);
  };

  const handleProfileClick = () => {
    setUserDropdownOpen(false);
    setCurrentPage(PAGES.PROFILE);
  };

  const handleBookingHistoryClick = () => {
    setUserDropdownOpen(false);
    setCurrentPage(PAGES.BOOKING_HISTORY);
  };

  // Existing handlers
  const handleNavClick = (service) => {
    const serviceType = service === 'airport' ? SERVICE_TYPES.AIRPORT : SERVICE_TYPES.CITY;
    setSelectedService(serviceType);
    setCurrentPage(PAGES.HOME);
  };

  const handleCurrencyChange = (currencyCode) => {
    changeCurrency(currencyCode);
    setShowCurrencyDropdown(false);
  };

  // Global premium header classes
  const headerClasses = "glass-panel relative z-20 transition-all duration-300";
  
  // Adjusted styles for global use
  const headerStyle = {
    borderRadius: '0 0 24px 24px',
    borderTop: 'none',
    padding: '12px 0',
    background: transparent ? 'rgba(13, 19, 32, 0.4)' : 'rgba(13, 19, 32, 0.8)',
  };

  const navLinkClasses = (active) => `cursor-pointer transition-colors text-sm font-medium ${active ? 'text-white font-semibold' : 'text-gray-400 hover:text-white'}`;

  const buttonClasses = "bg-blue-600 text-white rounded-full px-3 md:px-4 py-1.5 text-[10px] md:text-xs hover:bg-blue-700 transition-all font-black tracking-widest uppercase";

  return (
    <>
      <header className={headerClasses} style={headerStyle}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex justify-between items-center w-full">
            
            {/* Logo Section */}
            <div className="flex items-center gap-8">
              <div 
                className="flex items-center cursor-pointer group"
                onClick={() => setCurrentPage(PAGES.HOME)}
              >
                <img 
                  src={logoMark} 
                  alt="LankaWayFinder" 
                  className="h-10 lg:h-12 w-auto transition-transform group-hover:scale-105"
                />
              </div>
              
              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center gap-8">
                <span 
                  className={navLinkClasses(selectedService === SERVICE_TYPES.AIRPORT)}
                  onClick={() => handleNavClick('airport')}
                >
                  Airport ride
                </span>
                <span 
                  className={navLinkClasses(selectedService === SERVICE_TYPES.CITY)}
                  onClick={() => handleNavClick('city')}
                >
                  City rides
                </span>
                <span 
                  className={navLinkClasses(currentPage === PAGES.FAQ)}
                  onClick={() => setCurrentPage(PAGES.FAQ)}
                >
                  FAQ
                </span>
              </nav>
            </div>
            
            {/* Right Side Actions */}
            <div className="flex items-center gap-4">
              {/* Currency Selector */}
              <div className="relative">
                <button
                  onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                  className="bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-full px-2 md:px-3 py-1.5 text-[10px] md:text-xs cursor-pointer transition-all flex items-center gap-1 md:gap-2 font-medium"
                >
                  <span className="opacity-80">{selectedCurrency?.symbol || '$'}</span>
                  <span className="font-bold">{selectedCurrency?.code || 'USD'}</span>
                  <span className="text-[8px] opacity-40">▼</span>
                </button>
                
                {showCurrencyDropdown && (
                  <div className="absolute top-full right-0 mt-3 bg-[#0a0f1a] text-white rounded-xl shadow-2xl border border-white/10 min-w-[130px] z-50 overflow-hidden">
                    {currencies.map((currency) => (
                      <button
                        key={currency.code}
                        onClick={() => handleCurrencyChange(currency.code)}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 transition-colors flex items-center justify-between ${
                          selectedCurrency?.code === currency.code ? 'bg-white/5 font-semibold text-blue-400' : ''
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="opacity-60">{currency.symbol}</span>
                          <span>{currency.code}</span>
                        </span>
                        {selectedCurrency?.code === currency.code && (
                          <span className="text-blue-400 font-bold">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Language - Hidden on mobile */}
              <div className="hidden md:flex bg-white/5 border border-white/10 text-white rounded-full px-3 py-1.5 text-xs font-medium">
                EN
              </div>

              {/* Authentication Section */}
              {isAuthenticated && user ? (
                <div className="relative">
                  <button
                    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    className="flex items-center gap-2 bg-blue-600 text-white rounded-full px-3 py-1.5 text-xs hover:bg-blue-700 transition-all font-bold"
                  >
                    <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center text-blue-600 text-[10px] font-bold">
                      {user.firstName ? user.firstName[0].toUpperCase() : 'U'}
                    </div>
                    <span className="hidden md:inline">{user.firstName || 'User'}</span>
                    <svg className={`w-3 h-3 transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {userDropdownOpen && (
                    <div className="absolute right-0 mt-3 w-52 bg-[#0a0f1a] text-white rounded-xl shadow-2xl border border-white/10 py-2 z-50 overflow-hidden">
                      <div className="px-4 py-3 border-b border-white/5 bg-white/5">
                        <div className="font-bold text-sm">{user.firstName} {user.lastName}</div>
                        <div className="text-xs text-gray-400 truncate mt-0.5">{user.email}</div>
                      </div>
                      <button onClick={handleProfileClick} className="w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 transition-colors">Profile</button>
                      <button onClick={handleBookingHistoryClick} className="w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 transition-colors">Bookings</button>
                      <button onClick={() => { setUserDropdownOpen(false); setCurrentPage(PAGES.PROFILE_SETTINGS); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 transition-colors">Settings</button>
                      <hr className="my-1 border-white/5" />
                      <button onClick={handleLogout} className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-red-400/10 transition-colors">Sign Out</button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 md:gap-3">
                  <button
                    onClick={openLoginModal}
                    className="text-white hover:text-blue-500 text-[10px] md:text-xs font-black uppercase tracking-widest transition-colors px-2 py-1.5"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={openRegisterModal}
                    className={buttonClasses}
                  >
                    SIGN UP
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Authentication Modals */}
      {loginModalOpen && (
        <LoginModal
          onClose={closeLoginModal}
          onSwitchToRegister={handleSwitchToRegister}
        />
      )}
      {registerModalOpen && (
        <RegisterModal
          onClose={closeRegisterModal}
          onSwitchToLogin={handleSwitchToLogin}
        />
      )}
      
      {/* Click-away overlays */}
      {(userDropdownOpen || showCurrencyDropdown) && (
        <div className="fixed inset-0 z-10" onClick={() => { setUserDropdownOpen(false); setShowCurrencyDropdown(false); }} />
      )}
    </>
  );
};

export default Header;